import hoistNonReactStatics from 'hoist-non-react-statics';
import React, { FC, useContext, useMemo } from 'react';
import { ScreenClassContext } from 'react-grid-system';
import { withTheme } from 'styled-components';

import withSchema from 'ia-react-core/~/hocs/schema/withSchema';
import { ModalState } from 'ia-react-core/~/interfaces/ModalState';
import { ParametersExceptFirst } from 'ia-react-core/~/interfaces/ParametersExceptFirst';
import { ActionMap } from 'ia-react-core/~/interfaces/ReducerActions';

import closeModalById from '../../../actions/modal-fn/closeModalById';
import openModalById from '../../../actions/modal-fn/openModalById';
import injectAnalyticsScope from '../../../hocs/analytics/injectAnalyticsScope';
import withUniqueId from '../../../hocs/helpers/#withUniqueId';
import injectReducer from '../../../hocs/redux/injectReducer';
import compose from '../../../utils/components/#compose';

import ConnectedModal from './ConnectedModal';
import Modal, { ModalComposedProps, ModalProps } from './Modal';
import modalReducer from './Modal.reducer';
import RootContext from '../../config/#RootContext';

export * from './Modal';

const ComposedModal: (<Data>(
  props: ModalProps<Data>
) => React.ReactElement<ModalProps<Data>>) & {
  displayName: string;
  type: string;
  Content: typeof Modal.Content;
  Footer: typeof Modal.Footer;
  publicProps: typeof Modal.publicProps;
  setAppElement: typeof Modal.setAppElement;
} = compose<any>(
  withUniqueId('modalId'),
  withSchema('modal', { skip: ({ isOpened }) => typeof isOpened !== 'undefined' }),
  injectAnalyticsScope<ModalComposedProps>({
    key: ({ name }) => name,
  }),
  injectReducer<ModalComposedProps, ModalState>({
    key: ({ name }) => name,
    idName: 'modalId',
    namespace: 'Core/Modal',
    skip: ({ isOpened }) => typeof isOpened !== 'undefined',
    identifiers: ({ modalId, name }) => ({ modalId, name }),
    reducer: modalReducer,
    actions: ({ modalId: currentModalId }) => ({
      openModal: (...args: ParametersExceptFirst<typeof openModalById>) => openModalById(currentModalId, ...args),
      closeModal: (...args: ParametersExceptFirst<typeof closeModalById>) => closeModalById(currentModalId, ...args),
    } as ActionMap<'modalId'>),
    initialState: ({ modalId, name, initialIsOpened }) => ({
      modalId,
      name,
      isOpened: Boolean(initialIsOpened),
    }),
    useScope: true,
    createScope: false,
    cleanState: true,
  }),
  (WrappedComponent: FC<ModalComposedProps>) => {
    const WithConfig = (props: ModalComposedProps) => {
      const upperBreakpoint = useContext(ScreenClassContext);
      const isMobile = useMemo(() => ['xs', 'sm'].includes(upperBreakpoint), [upperBreakpoint]);
      const { root } = useContext(RootContext);
      return (
        <WrappedComponent
          {...props}
          isMobile={isMobile}
          root={root}
        />
      );
    };

    return hoistNonReactStatics(WithConfig, WrappedComponent);
  },
  withTheme,
)(ConnectedModal);

ComposedModal.displayName = 'Modal';

export default ComposedModal;
